import { GameProps } from '@/components/Gameinterfaces';
import { GameConfig } from '@/config/game';
import { GameStep } from '@/config/game/steps';
import getRandomNumberArray from '@/utilities/getRandomNumberArray';
import { findSubsidiaryByKey } from '@/utilities/subsidiaries';
import { cleanURLQueryParams, URLCleanModes } from './GameQueryParamsCleaner';

export interface GameQueryParamsParserInterface {
  gameProps: Partial<GameProps>;
  couponGroup: GameConfig['couponGroup'];
  queryParams: { [key: string]: any };
  paramMapper: { [key: string]: any };
}

export default class GameQueryParamsParser implements GameQueryParamsParserInterface {
  gameProps: GameProps;
  couponGroup: GameConfig['couponGroup'];
  queryParams: { [key: string]: any };
  paramMapper: { [key: string]: any };

  constructor(gameProps: GameProps) {
    this.gameProps = gameProps;
    this.couponGroup = gameProps.couponGroup;
    this.queryParams = this.getUrlParams(window.location.search);

    this.paramMapper = {
      f: this.couponParser,
      a: this.couponParser,
      ln: this.playNumberParser,
      az: this.drawNumberParser,
      zt: this.drawDayParser,
      ei: this.drawRateParser,
      ba: this.drawRateParser,
      st: this.stepParser,
      s: this.subsidiaryParser,
    };
  }

  parse(removeParamsAfterParse: boolean = true) {
    if (!this.queryParams) {
      return;
    }

    const paramMapper = {
      ...this.paramMapper,
      ...this.additionalGameParamMapper(),
    };

    Object.keys(this.queryParams).forEach((queryParamKey) => {
      const queryParamKeyLower = queryParamKey.toLowerCase();
      const key = this.getParamMapKey(queryParamKeyLower, paramMapper);
      const queryParamsValueLower = (
        this.queryParams[queryParamKey] || ''
      ).toLowerCase();
      if (key) {
        const parser = paramMapper[key];
        if (parser) {
          parser(queryParamKeyLower, queryParamsValueLower);
        }
      }
    });

    if (removeParamsAfterParse) {
      cleanURLQueryParams(URLCleanModes.REPLACE);
    }
  }

  private getUrlParams = (search: string) =>
    `${search}?`
      .split('?')[1]
      .split('&')
      .reduce((params: object, pair: string) => {
        const [key, value] = `${pair}=`.split('=').map(decodeURIComponent);

        return key.length > 0 ? { ...params, [key]: value } : params;
      }, {});

  private couponParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    if (!this.couponGroup) {
      return;
    }

    const groupParam = +queryParamKey.replace('a', '');
    const couponId = groupParam ? groupParam : +queryParamKey.replace('f', '');
    const groupId = this.couponGroup.numberGroups.length && groupParam ? 1 : 0;
    const couponGroupsConfig = this.couponGroup.numberGroups[groupId];

    if (couponGroupsConfig) {
      if (/^qt(-\d?\d)?$/.test(queryParamValue) && !groupParam) {
        this.couponGroup.numberGroups.forEach((group, groupId) => {
          let dynamicSize;

          if (couponGroupsConfig.minCrossed !== couponGroupsConfig.maxCrossed) {
            dynamicSize = +queryParamValue.replace('qt-', '');
            if (
              dynamicSize > group.maxCrossed ||
              dynamicSize < group.minCrossed
            ) {
              return;
            }
          }

          const size = dynamicSize || group.maxCrossed;
          const numbers = getRandomNumberArray(
            size,
            group.startValue,
            group.amountNumbers,
            true
          );
          this.gameProps.onCouponUpdate(
            this.gameProps.id,
            couponId,
            groupId,
            numbers
          );
        });
      } else {
        const numbers = queryParamValue
          .split(',')
          .reduce((previousValue: number[], currentValue: string) => {
            const value = +currentValue;
            if (
              !isNaN(value) &&
              couponGroupsConfig &&
              couponGroupsConfig.amountNumbers >= value
            ) {
              previousValue.push(value);
            }

            return previousValue;
          }, []);

        if (
          couponGroupsConfig.minCrossed <= numbers.length &&
          couponGroupsConfig.maxCrossed >= numbers.length
        ) {
          this.gameProps.onCouponUpdate(
            this.gameProps.id,
            couponId,
            groupId,
            numbers
          );
        }
      }
    }
  };

  private playNumberParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    const { playNumber, onPlayNumbersUpdate } = this.gameProps;
    if (playNumber) {
      const values = queryParamValue
        .split('')
        .reduce((previousValue: number[], currentValue: string) => {
          const value = +currentValue;
          if (
            !isNaN(value) &&
            playNumber.valueMin <= value &&
            playNumber.valueMax >= value
          ) {
            previousValue.push(value);
          }
          return previousValue;
        }, []);

      if (playNumber.amountNumbers === values.length) {
        onPlayNumbersUpdate(this.gameProps.id, values);
      }
    }
  };

  private additionalGameParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    const additionalGame =
      this.gameProps.additionalGames &&
      this.gameProps.additionalGames.find(
        (game) => !!game.queryParamKey && game.queryParamKey === queryParamKey
      );

    if (
      additionalGame &&
      additionalGame.additionalGameId &&
      +queryParamValue === 1 &&
      !this.gameProps.selectedAdditionalGames.some(
        (gameId) => gameId === additionalGame.additionalGameId
      )
    ) {
      this.gameProps.onAdditionalGameToggle(
        this.gameProps.id,
        additionalGame.additionalGameId
      );
    }
  };

  private drawNumberParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    const weeksNumber = +queryParamValue;
    if (weeksNumber) {
      if (this.gameProps.drawNumbers) {
        this.gameProps.onDrawNumberUpdate(this.gameProps.id, weeksNumber);
      } else if (
        this.gameProps.weeksNumber &&
        this.gameProps.weeksNumber.some((item) => item.value === weeksNumber)
      ) {
        this.gameProps.onWeeksNumberUpdate(this.gameProps.id, weeksNumber);
      }
    }
  };

  private drawDayParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    if (this.gameProps.drawDays) {
      const drawDay = this.gameProps.drawDays.options.find(
        (option) =>
          option
            .map((item) => item.slice(0, 2).toLocaleLowerCase())
            .join('') === queryParamValue
      );

      if (drawDay) {
        this.gameProps.onDrawDaysUpdate(this.gameProps.id, drawDay);
      }
    }
  };

  private drawRateParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    const drawRate =
      queryParamKey === 'ei' ? +queryParamValue / 100 : +queryParamValue;

    if (
      this.gameProps.drawRate &&
      !isNaN(drawRate) &&
      drawRate > 0 &&
      this.gameProps.drawRate.some((item) => item.value === drawRate)
    ) {
      this.gameProps.onDrawRateUpdate(this.gameProps.id, drawRate);
    }
  };

  private stepParser = (queryParamKey: string, paramMapper: string) => {
    const paramStep =
      Object.keys(GameStep).find(
        (step) => step.toLocaleLowerCase() === paramMapper
      ) || GameStep.Summary;
    this.gameProps.onCurrentStepUpdate(this.gameProps.id, paramStep);
  };

  private getParamMapKey: (
    queryParamKey: string,
    paramMapper: object
  ) => string | undefined = (queryParamKey: string, paramMapper: object) => {
    const convertedQueryKey = queryParamKey
      .toLowerCase()
      .replace(/([fa])+\d+$/g, '$1');
    return Object.keys(paramMapper).find((key) => convertedQueryKey === key);
  };

  private additionalGameParamMapper = ():
    | { [key: string]: any }
    | undefined => {
    return (
      this.gameProps.additionalGames &&
      this.gameProps.additionalGames.reduce(
        (prevValue: { [key: string]: any }, curValue: GameConfig) => {
          if (curValue.queryParamKey) {
            prevValue[curValue.queryParamKey] = this.additionalGameParser;
          }
          return prevValue;
        },
        {}
      )
    );
  };

  private subsidiaryParser: (
    queryParamKey: string,
    queryParamValue: string
  ) => void = (queryParamKey: string, queryParamValue: string) => {
    if (queryParamValue) {
      const sub = findSubsidiaryByKey(queryParamValue.toUpperCase());
      if (sub) {
        this.gameProps.onSubsidiaryUpdate(sub.id);
      }
    }
  };
}
